import ErrorBoundary from "@/components/common/ErrorBoundary";
import PageHeader from "@/components/common/PageHeader";
import DataTable from "@/components/datatable/components/DataTable";
import { safeDecompress, tableSearchSchema } from "@/components/datatable/hooks/useTableUrlState";
import { mapTableStateToQueryParams } from "@/components/datatable/utils";
import useConfirm from "@/hooks/useConfirm";
import { salariesKeys } from "@/query-keys";
import type { SalaryListItem } from "@/schemas/salarySchema";
import { services } from "@/services";
import { createFileRoute } from "@tanstack/react-router";
import { useCallback } from "react";
import { useTranslation } from "react-i18next";
import NewSalary from "./_components/newSalary";
import useColumns from "./_hooks/useColumns";
import useSalariesQuery from "./_hooks/useSalariesQuery";
import useSalaryMutation from "./_hooks/useSalaryMutation";

export const Route = createFileRoute("/_private/employees/$employeeId/salaries/")({
  validateSearch: tableSearchSchema,
  loaderDeps: ({ search }) => ({
    q: search.q,
  }),
  loader: async ({ params: { employeeId }, deps, context: { queryClient } }) => {
    const tableState = safeDecompress(deps.q);
    const requestParams = mapTableStateToQueryParams(tableState);
    return queryClient.ensureQueryData({
      queryKey: salariesKeys.lists(employeeId, deps.q),
      queryFn: () => services.getEmployeeSalaries(employeeId, requestParams),
    });
  },
  component: RouteComponent,
  errorComponent: ({ error }) => (
    <ErrorBoundary error={error} reset={() => window.location.reload()} />
  ),
});

function RouteComponent() {
  const { employeeId } = Route.useParams();
  const search = Route.useSearch();
  const loaderData = Route.useLoaderData();

  const columns = useColumns();
  const { t } = useTranslation();

  const { data: paginatedSalaries } = useSalariesQuery(employeeId, search.q, loaderData);
  const { deleteSalary, isDeleting } = useSalaryMutation(employeeId);

  const deleteConfirm = useConfirm({
    title: t("salaries.confirm.delete.title"),
    description: t("salaries.confirm.delete.description"),
    isPending: isDeleting,
  });

  const handleDelete = useCallback(
    (salary: SalaryListItem) => deleteConfirm.confirm(() => void deleteSalary(salary.id)),
    [deleteConfirm, deleteSalary],
  );

  return (
    <>
      <PageHeader
        title={t("salaries.list.title")}
        subtitle={t("salaries.list.subtitle")}
        action={<NewSalary employeeId={employeeId} />}
      />
      <DataTable<SalaryListItem>
        searchPlaceholder={t("salaries.list.searchPlaceholder")}
        columns={columns}
        data={paginatedSalaries?.items}
        nextCursor={paginatedSalaries?.nextCursor}
        hasMore={paginatedSalaries?.hasMore}
        queryKey={salariesKeys.lists(employeeId, search.q)}
        navigateFrom={"/employees/$employeeId/salaries"}
        searchFrom={"/_private/employees/$employeeId/salaries/"}
        onDelete={handleDelete}
        searchable={true}
      />
      {deleteConfirm.confirmDialog}
    </>
  );
}
